import { ConversationTemplateSchema } from "@samklang/schemas";
import { agreement, multi, scaleFive, tell, text, yesNo } from "./builders";

/**
 * Multi-perspective kartläggning (assessment) template: "Trivsel och skolsituation".
 *
 * Designed to collect the same overarching areas from several perspectives
 * (Elev, Mentor, Vårdnadshavare, Annan professionell) so answers can be
 * compared. Every question carries per-perspective wording so the exact same
 * area appears naturally from each point of view.
 *
 * This is one generic example (not USK-hardcoded) — the same pattern is
 * reusable for skolsociala utredningar, frånvaroutredningar, socialpedagogiska
 * bedömningar, elevhälsans kartläggningar, uppföljning av stöd och
 * mentorssamtal (spec point 24).
 */
export const kartlaggningTrivselConversation = ConversationTemplateSchema.parse({
  id: "kartlaggning-trivsel",
  version: 1,
  locale: "sv-SE",
  title: "Trivsel och skolsituation",
  description: "Samla elevens, mentorns, vårdnadshavarens och andra professionellas perspektiv på skolsituationen och jämför dem i en samlad bild.",
  icon: "🧭",
  category: "Kartläggning",
  audience: ["Elev", "Mentor", "Vårdnadshavare", "Professionell"],
  estimatedMinutes: 20,
  status: "published",
  reviewedAt: "2026-09-02",
  reviewerRoles: ["Produkt", "Specialpedagog", "Elevhälsa"],
  keywords: ["kartläggning", "trivsel", "skolsituation", "flera perspektiv"],
  perspectives: ["elev", "mentor", "vardnadshavare", "annan"],
  questions: [
    scaleFive("trivsel-01", "Hur trivs du i skolan?",
      "Väldigt dåligt", "Väldigt bra",
      {
        section: "Trivsel",
        summaryCategory: "important",
        priority: 100,
        metric: { key: "trivsel", label: "Trivsel", direction: "higher_is_better" },
        wordingByPerspective: {
          elev: "Hur trivs du i skolan?",
          mentor: "Hur upplever du att eleven trivs i skolan?",
          vardnadshavare: "Hur upplever du att ditt barn trivs i skolan?",
          annan: "Hur upplever du, utifrån din roll och kontakt med eleven, att eleven trivs i skolan?",
        },
        wordingByAudience: {
          enkel: "Känns skolan bra för dig?",
          younger: "Känns skolan bra för dig?",
        },
      }),
    agreeScale("trivsel-02", "Eleven känner sig trygg i skolan", {
      elev: "Känner du dig trygg i skolan?",
      mentor: "Eleven känner sig trygg i skolan",
      vardnadshavare: "Mitt barn känner sig tryggt i skolan",
      annan: "Eleven framstår som trygg i skolan",
    }),
    scaleFive("trivsel-03", "Hur känns det att gå till skolan på morgonen?",
      "Väldigt jobbigt", "Väldigt bra",
      {
        section: "Trivsel",
        summaryCategory: "important",
        wordingByPerspective: {
          elev: "Hur känns det att gå till skolan på morgonen?",
          mentor: "Hur verkar eleven må när den kommer till skolan på morgonen?",
          vardnadshavare: "Hur upplever du att det känns för ditt barn att gå till skolan på morgonen?",
          annan: "Hur upplever du, utifrån din roll och kontakt med eleven, att eleven mår när den kommer till skolan?",
        },
        wordingByAudience: { enkel: "Hur känns det på morgonen innan skolan?", younger: "Hur känns det på morgonen innan skolan?" },
      }),
    multi("trivsel-04", "Vad fungerar bra just nu i skolan?",
      [{ id: "subjects", label: "Ämnen / undervisning", symbol: "📚" }, { id: "friends", label: "Kompisar", symbol: "👥" }, { id: "teachers", label: "Vuxna i skolan", symbol: "🧑‍🏫" }, { id: "breaks", label: "Raster", symbol: "⏸" }, { id: "activities", label: "Fritids / aktiviteter", symbol: "⭐" }, { id: "routines", label: "Tydliga rutiner", symbol: "🗓️" }],
      {
        section: "Det som fungerar",
        summaryCategory: "works",
        wordingByPerspective: {
          elev: "Vad fungerar bra just nu i skolan?",
          mentor: "Vad fungerar bra för eleven i skolan just nu?",
          vardnadshavare: "Vad fungerar bra för ditt barn i skolan just nu?",
          annan: "Vad upplever du fungerar bra för eleven just nu, utifrån din roll och kontakt med eleven?",
        },
      }),
    multi("trivsel-05", "Vad är svårt just nu?",
      [{ id: "subjects", label: "Ämnen / undervisning", symbol: "📚" }, { id: "concentration", label: "Koncentration", symbol: "🎯" }, { id: "friends", label: "Kompisar / socialt", symbol: "👥" }, { id: "breaks", label: "Raster", symbol: "⏸" }, { id: "environment", label: "Ljud / miljö", symbol: "🔊" }, { id: "demands", label: "Krav och förväntningar", symbol: "▦" }, { id: "changes", label: "Förändringar", symbol: "↔" }],
      {
        section: "Det som är svårt",
        summaryCategory: "hard",
        wordingByPerspective: {
          elev: "Är det något som är svårt just nu?",
          mentor: "Vad upplever du är svårt för eleven just nu?",
          vardnadshavare: "Vad upplever du är svårt för ditt barn just nu?",
          annan: "Vad upplever du är svårt för eleven just nu, utifrån din roll och kontakt med eleven?",
        },
      }),
    scaleFive("trivsel-06", "Hur känns det i undervisningen?",
      "Väldigt jobbigt", "Väldigt bra",
      {
        section: "Undervisning",
        summaryCategory: "important",
        wordingByPerspective: {
          elev: "Hur känns det i undervisningen?",
          mentor: "Hur fungerar eleven i undervisningen?",
          vardnadshavare: "Hur upplever du att det känns för ditt barn i undervisningen?",
          annan: "Hur upplever du att undervisningssituationen fungerar för eleven, utifrån din roll och kontakt med eleven?",
        },
        wordingByAudience: { enkel: "Känns lektionerna bra för dig?", younger: "Känns lektionerna bra för dig?" },
      }),
    multi("trivsel-07", "Vad fungerar bra i undervisningen?",
      [{ id: "clear", label: "Tydliga instruktioner", symbol: "🧭" }, { id: "help", label: "Att få hjälp av vuxen", symbol: "🤝" }, { id: "own-pace", label: "Få jobba i egen takt", symbol: "🐢" }, { id: "interesting", label: "Intressanta uppgifter", symbol: "💡" }, { id: "alone", label: "Få jobba själv", symbol: "🧘" }, { id: "small", label: "Liten grupp", symbol: "👥" }],
      {
        section: "Undervisning",
        summaryCategory: "works",
        wordingByPerspective: {
          elev: "Vad fungerar bra i undervisningen?",
          mentor: "Vad fungerar bra för eleven i undervisningen?",
          vardnadshavare: "Vad fungerar bra för ditt barn i undervisningen?",
          annan: "Vad upplever du fungerar bra i undervisningen för eleven, utifrån din roll och kontakt med eleven?",
        },
      }),
    multi("trivsel-08", "Vad skulle göra undervisningen lättare?",
      [{ id: "clear", label: "Tydligare instruktioner", symbol: "🧭" }, { id: "small", label: "Mindre sammanhang", symbol: "👥" }, { id: "help", label: "Mer hjälp av vuxen", symbol: "🤝" }, { id: "quiet", label: "Lugnare arbetsmiljö", symbol: "🔉" }, { id: "breaks", label: "Fler pauser", symbol: "⏸" }, { id: "structure", label: "Tydligare struktur", symbol: "🗓️" }],
      {
        section: "Undervisning",
        summaryCategory: "change",
        wordingByPerspective: {
          elev: "Vad skulle göra undervisningen lättare för dig?",
          mentor: "Vad skulle göra undervisningen lättare för eleven?",
          vardnadshavare: "Vad tror du skulle göra undervisningen lättare för ditt barn?",
          annan: "Vad upplever du skulle göra undervisningen lättare för eleven, utifrån din roll och kontakt med eleven?",
        },
      }),
    scaleFive("trivsel-09", "Hur fungerar det socialt i skolan?",
      "Väldigt svårt", "Väldigt bra",
      {
        section: "Socialt",
        summaryCategory: "important",
        wordingByPerspective: {
          elev: "Hur fungerar det med kompisar i skolan?",
          mentor: "Hur fungerar det socialt för eleven i skolan?",
          vardnadshavare: "Hur upplever du att det sociala fungerar för ditt barn i skolan?",
          annan: "Hur upplever du att det sociala fungerar för eleven, utifrån din roll och kontakt med eleven?",
        },
        wordingByAudience: { enkel: "Hur är det med kompisar i skolan?", younger: "Hur är det med kompisar i skolan?" },
      }),
    agreement("trivsel-10", "Eleven har någon att vara med", {
      section: "Socialt",
      summaryCategory: "works",
      wordingByPerspective: {
        elev: "Har du någon att vara med?",
        mentor: "Eleven har någon att vara med",
        vardnadshavare: "Mitt barn har någon att vara med",
        annan: "Eleven har någon att vara med",
      },
    }),
    yesNo("trivsel-11", "Får eleven hjälp när den behöver?",
      {
        section: "Stöd",
        summaryCategory: "works",
        wordingByPerspective: {
          elev: "Kan du få hjälp när du behöver?",
          mentor: "Får eleven hjälp när den behöver?",
          vardnadshavare: "Får ditt barn hjälp när det behöver?",
          annan: "Får eleven hjälp när den behöver?",
        },
      }),
    multi("trivsel-12", "Vad behöver eleven mest just nu?",
      [{ id: "adult", label: "En vuxen att prata med", symbol: "👂" }, { id: "calm", label: "Lugn och ro", symbol: "🌿" }, { id: "support", label: "Stöd i undervisningen", symbol: "🤝" }, { id: "friends", label: "Hjälp med kompisar", symbol: "👥" }, { id: "clarity", label: "Tydlighet", symbol: "🧭" }, { id: "routines", label: "Stadiga rutiner", symbol: "🗓️" }],
      {
        section: "Behov",
        summaryCategory: "change",
        wordingByPerspective: {
          elev: "Vad behöver du mest just nu?",
          mentor: "Vad upplever du att eleven behöver mest just nu?",
          vardnadshavare: "Vad upplever du att ditt barn behöver mest just nu?",
          annan: "Vad upplever du att eleven behöver mest just nu, utifrån din roll och kontakt med eleven?",
        },
      }),
    tell("trivsel-13", "Vad tycker du är viktigast att vi tar med oss?",
      {
        section: "Avslut",
        summaryCategory: "understand",
        priority: 90,
        wordingByPerspective: {
          elev: "Vad tycker du är viktigast att vi tar med oss från samtalet?",
          mentor: "Vad tycker du är viktigast att ta med i kartläggningen?",
          vardnadshavare: "Vad tycker du är viktigast att ta med i kartläggningen?",
          annan: "Vad upplever du är viktigast att ta med i kartläggningen, utifrån din roll och kontakt med eleven?",
        },
      }),
    text("trivsel-14", "Något mer som är viktigt att känna till?",
      {
        section: "Avslut",
        summaryCategory: "other",
        wordingByPerspective: {
          elev: "Något mer du vill berätta?",
          mentor: "Något mer som är viktigt att känna till?",
          vardnadshavare: "Något mer som är viktigt att känna till om ditt barn?",
          annan: "Något mer som är viktigt att känna till, utifrån din roll och kontakt med eleven?",
        },
      }),
    text("trivsel-15", "Vad fungerar när det är svårt? (professionellt stöd)",
      {
        section: "Professionell bedömning",
        summaryCategory: "works",
        priority: 20,
        wordingByPerspective: {
          mentor: "Vad brukar fungera för eleven när det är svårt?",
          vardnadshavare: "Vad brukar fungera för ditt barn när det är svårt?",
          annan: "Vad brukar fungera för eleven när det är svårt, utifrån din roll och kontakt med eleven?",
        },
      }),
    text("trivsel-16", "Vilka styrkor ser du hos eleven? (professionellt stöd)",
      {
        section: "Professionell bedömning",
        summaryCategory: "gives_energy",
        priority: 20,
        wordingByPerspective: {
          elev: "Vad är du bra på?",
          mentor: "Vilka styrkor ser du hos eleven?",
          vardnadshavare: "Vilka styrkor ser du hos ditt barn?",
          annan: "Vilka styrkor ser du hos eleven, utifrån din roll och kontakt med eleven?",
        },
      }),
    text("trivsel-17", "Vilka anpassningar testas just nu? (professionellt stöd)",
      {
        section: "Professionell bedömning",
        summaryCategory: "change",
        priority: 20,
        wordingByPerspective: {
          elev: "Finns det något i skolan som hjälper dig? Vad fungerar?",
          mentor: "Vilka anpassningar eller åtgärder finns just nu, och hur fungerar de?",
          vardnadshavare: "Vilka anpassningar finns just nu, och hur upplever du att de fungerar?",
          annan: "Vilka anpassningar eller åtgärder finns just nu, och vad observerar du kring effekten?",
        },
      }),
    text("trivsel-18", "Vad skulle behöva utforskas vidare? (professionellt stöd)",
      {
        section: "Professionell bedömning",
        summaryCategory: "understand",
        priority: 20,
        wordingByPerspective: {
          mentor: "Vad skulle behöva utforskas vidare kring eleven?",
          vardnadshavare: "Vad skulle behöva utforskas vidare kring ditt barn?",
          annan: "Vad skulle behöva utforskas vidare, utifrån din roll och kontakt med eleven?",
        },
      }),
  ],
});

type AgreeDetails = {
  readonly elev?: string;
  readonly mentor?: string;
  readonly vardnadshavare?: string;
  readonly annan?: string;
  readonly section?: string;
  readonly summaryCategory?: "works" | "important" | "hard" | "change" | "understand" | "gives_energy" | "takes_energy" | "other";
};

function agreeScale(id: string, baseTitle: string, wording: AgreeDetails) {
  return agreement(id, baseTitle, {
    section: wording.section ?? "Trivsel",
    summaryCategory: wording.summaryCategory ?? "important",
    wordingByPerspective: {
      elev: wording.elev ?? baseTitle,
      mentor: wording.mentor ?? baseTitle,
      vardnadshavare: wording.vardnadshavare ?? baseTitle,
      annan: wording.annan ?? baseTitle,
    },
  });
}
